<template>
  <div class="px-3">
    <NuxtLink
      v-if="
        selectedBusiness &&
        organization.isBusinessesEnabled &&
        organization.organizationSettings?.businessSelectorEnabled &&
        businessesCount > 1
      "
      to="/business-selection"
      class="block rounded-lg w-full p-2 mb-5 text-on-surface leading-none font-semibold cursor-pointer"
    >
      {{ selectedBusiness.name }}
    </NuxtLink>

    <Dropdown v-if="authUserStore.isLoggedIn">
      <template #activator="{ attrs, toggleDropdownVisibility }">
        <button
          v-bind="attrs"
          class="flex items-center h-8 rounded-lg px-2 mb-6 text-on-surface leading-none font-semibold"
          data-testid="profile-dropdown"
          @click="toggleDropdownVisibility"
        >
          <span
            class="bg-secondary text-on-secondary w-8 h-8 rounded-full flex items-center justify-center mr-3 text-xs font-bold"
          >
            {{ authUserStore.initials }}
          </span>
          {{ authUserStore.authUser.fullName }}
        </button>
      </template>
      <template #default="{ closeDropdown }">
        <template v-for="option in dropdownOptions">
          <NuxtLink
            v-if="option.to"
            :key="option.key"
            :to="option.to"
            class="flex items-center h-10 rounded-lg px-2 text-sm text-on-surface leading-none gap-2"
            @click="closeDropdown"
          >
            <icon :icon="option.icon" />
            {{ option.label }}
          </NuxtLink>
          <button
            v-else
            :key="option.key"
            type="button"
            class="flex items-center h-10 rounded-lg px-2 text-sm text-on-surface leading-none gap-2"
            @click="option.handler"
          >
            <Icon :icon="option.icon" />
            {{ option.label }}
          </button>
        </template>
      </template>
    </Dropdown>

    <Btn
      v-if="authUserStore.isLoggedIn && adminOrganizations?.totalCount !== 0"
      :variant="BtnVariant.FILLED"
      :size="BtnSize.LG"
      :color="BtnColor.PRIMARY"
      :to="config.public.adminAppUrl"
      class="mb-5 w-full"
    >
      {{ $t('layout.admin-panel') }}
    </Btn>
    <Btn
      v-if="!authUserStore.isLoggedIn && organization.displaySignInButton"
      :variant="BtnVariant.FILLED"
      :size="BtnSize.LG"
      :color="BtnColor.PRIMARY"
      to="/auth/login"
      class="w-full"
    >
      {{ $t('layout.sign-in') }}
    </Btn>
  </div>
</template>

<script setup>
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { Dropdown } from '~/components/dropdown';
import { Icon } from '~/components/icon';
import { useAuthUserStore } from '~/store/auth-user';
import ProfileSvg from 'assets/icons/profile.svg';
import LogoutSvg from 'assets/icons/logout.svg';
import BusinessesSvg from 'assets/icons/briefcase.svg';
import { useAsyncData } from '#app';
import { apiGetOrganizationList } from '~/api/organizations';
import { AccountType } from '~/api/accounts';
import { useRuntimeConfig } from '#imports';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useBusinessesStore } from '~/store/businesses';
import ArrowDownSvg from 'assets/icons/arrow-down.svg';

const { t } = useI18n();
const authUserStore = useAuthUserStore();
const config = useRuntimeConfig();
const organizationStore = useOrganizationStore();
const businessesStore = useBusinessesStore();
const { organization } = storeToRefs(organizationStore);
const { businessesCount, selectedBusiness } = storeToRefs(businessesStore);

const { data: adminOrganizations } = useAsyncData('admin-organizations', () => {
  return apiGetOrganizationList({
    perPage: 1,
    accountType: AccountType.ADMIN,
  });
});

const dropdownOptions = computed(() => [
  {
    key: 'profile',
    icon: ProfileSvg,
    label: t('layout.profile'),
    to: '/profile',
  },
  {
    key: 'logout',
    icon: LogoutSvg,
    label: t('layout.logout'),
    to: '/auth/logout',
  },
]);
</script>
